import {
  ChevronDownIcon,
  ChevronRightIcon,
  CheckIcon,
  FileDirectoryFillIcon,
  FileDirectoryOpenFillIcon,
  FileIcon,
  FoldIcon,
  SearchIcon,
  SidebarCollapseIcon,
  SidebarExpandIcon,
  UnfoldIcon,
} from "@primer/octicons-react";
import { useEffect, useMemo, useState } from "react";
import type { DiffFile } from "../api/types";
import { pathTree, type PathNode } from "../lib/supervise";
import { cn } from "../lib/utils";
import { CopyButton, Empty } from "./common";
import { isMarkdown, MarkdownDiff } from "./MarkdownDiff";
import { Badge, type Tone } from "./ui/badge";
import { Button } from "./ui/button";
import { SegmentedControl } from "./ui/toggle-group";

type Kind = "add" | "del" | "ctx";
type Seg = { text: string; mark: boolean };
type Line = { kind: Kind; a?: number; b?: number; segs: Seg[] };
type Item = { hunk: string } | Line;

const isHunk = (i: Item): i is { hunk: string } => "hunk" in i;

const LINE: Record<Kind, { row: string; num: string }> = {
  add: { row: "bg-diff-add", num: "bg-diff-add-strong" },
  del: { row: "bg-diff-del", num: "bg-diff-del-strong" },
  ctx: { row: "", num: "bg-muted" },
};
const MARK: Record<Kind, string> = { add: "bg-diff-add-strong", del: "bg-diff-del-strong", ctx: "" };

const tokens = (s: string) => s.match(/\w+|\s+|[^\w\s]/g) ?? [];

/** Highlights the middle of a changed line pair: what is left after the shared token prefix and suffix. */
function wordDiff(del: string, add: string): [Seg[], Seg[]] {
  const x = tokens(del);
  const y = tokens(add);
  let p = 0;
  while (p < x.length && p < y.length && x[p] === y[p]) p++;
  let q = 0;
  while (q < x.length - p && q < y.length - p && x[x.length - 1 - q] === y[y.length - 1 - q]) q++;
  const cut = (t: string[]): Seg[] =>
    [
      { text: t.slice(0, p).join(""), mark: false },
      { text: t.slice(p, t.length - q).join(""), mark: true },
      { text: t.slice(t.length - q).join(""), mark: false },
    ].filter((g) => g.text);
  return [cut(x), cut(y)];
}

/** Parses a unified patch into hunk headers and numbered lines, pairing each deleted run with the added run after it for word-level marks. */
function parsePatch(patch: string): Item[] {
  const items: Item[] = [];
  let a = 0;
  let b = 0;
  let dels: Line[] = [];
  let adds: Line[] = [];
  const flush = () => {
    if (dels.length === adds.length) {
      dels.forEach((d, i) => {
        // A fully rewritten line is better left unmarked: the whole row already says so.
        const [ds, as] = wordDiff(d.segs[0].text, adds[i].segs[0].text);
        if (ds.length && as.length && ds.some((g) => !g.mark) && as.some((g) => !g.mark)) {
          d.segs = ds;
          adds[i].segs = as;
        }
      });
    }
    items.push(...dels, ...adds);
    dels = [];
    adds = [];
  };
  for (const line of patch.split("\n")) {
    if (line.startsWith("---") || line.startsWith("+++") || line.startsWith("\\") || line === "") continue;
    if (line.startsWith("@@")) {
      flush();
      const m = /@@ -(\d+)(?:,\d+)? \+(\d+)/.exec(line);
      if (m) {
        a = Number(m[1]);
        b = Number(m[2]);
      }
      items.push({ hunk: line });
    } else if (line.startsWith("+")) adds.push({ kind: "add", b: b++, segs: [{ text: line.slice(1), mark: false }] });
    else if (line.startsWith("-")) dels.push({ kind: "del", a: a++, segs: [{ text: line.slice(1), mark: false }] });
    else {
      flush();
      items.push({ kind: "ctx", a: a++, b: b++, segs: [{ text: line.slice(1), mark: false }] });
    }
  }
  flush();
  return items;
}

const NUM = "select-none px-3 text-right align-top text-muted-foreground";

function Code({ line }: { line: Line }) {
  return (
    <>
      {line.segs.map((g, i) => (
        <span key={i} className={cn(g.mark && cn("rounded-[3px]", MARK[line.kind]))}>
          {g.text}
        </span>
      ))}
    </>
  );
}

function HunkRow({ text, cols }: { text: string; cols: number }) {
  const m = /^(@@[^@]*@@)\s?(.*)$/.exec(text);
  return (
    <tr className="bg-diff-hunk text-info">
      <td colSpan={cols} className="px-4 py-1">
        <span>{m ? m[1] : text}</span>
        {m?.[2] && <span className="ml-2 text-muted-foreground">{m[2]}</span>}
      </td>
    </tr>
  );
}

const SIGN = { add: "+", del: "−", ctx: " " } as const;

/** One column of code with its sign, so wrapped lines hang under the text rather than under the sign. */
function Cell({ line, className }: { line?: Line; className?: string }) {
  if (!line) return <td className={cn("bg-muted/50", className)} />;
  return (
    <td className={cn("whitespace-pre-wrap break-words px-2 align-top", LINE[line.kind].row, className)}>
      <span className="mr-2 inline-block w-2 select-none text-muted-foreground">{SIGN[line.kind]}</span>
      <Code line={line} />
    </td>
  );
}

function Num({ n, line }: { n?: number; line?: Line }) {
  return <td className={cn(NUM, "w-[52px] min-w-[52px]", line ? LINE[line.kind].num : "bg-muted/50")}>{n ?? ""}</td>;
}

/** A unified patch: old and new line numbers, then one column of code. */
function UnifiedTable({ items }: { items: Item[] }) {
  return (
    <table className="w-full table-fixed border-collapse bg-background font-mono text-[13px] leading-5">
      <colgroup>
        <col style={{ width: 52 }} />
        <col style={{ width: 52 }} />
        <col />
      </colgroup>
      <tbody>
        {items.map((it, i) =>
          isHunk(it) ? (
            <HunkRow key={i} text={it.hunk} cols={3} />
          ) : (
            <tr key={i}>
              <Num n={it.a} line={it} />
              <Num n={it.b} line={it} />
              <Cell line={it} />
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
}

/** Side by side: deleted lines on the left, the added lines that replace them on the right. */
function SplitTable({ items }: { items: Item[] }) {
  const rows: Array<{ hunk: string } | { l?: Line; r?: Line }> = [];
  for (let i = 0; i < items.length; ) {
    const it = items[i];
    if (isHunk(it)) {
      rows.push(it);
      i++;
    } else if (it.kind === "ctx") {
      rows.push({ l: it, r: it });
      i++;
    } else {
      const dels: Line[] = [];
      const adds: Line[] = [];
      while (i < items.length && !isHunk(items[i]) && (items[i] as Line).kind === "del") dels.push(items[i++] as Line);
      while (i < items.length && !isHunk(items[i]) && (items[i] as Line).kind === "add") adds.push(items[i++] as Line);
      for (let k = 0; k < Math.max(dels.length, adds.length); k++) rows.push({ l: dels[k], r: adds[k] });
    }
  }
  return (
    <table className="w-full table-fixed border-collapse bg-background font-mono text-[13px] leading-5">
      <colgroup>
        <col style={{ width: 52 }} />
        <col />
        <col style={{ width: 52 }} />
        <col />
      </colgroup>
      <tbody>
        {rows.map((r, i) =>
          "hunk" in r ? (
            <HunkRow key={i} text={r.hunk} cols={4} />
          ) : (
            <tr key={i}>
              <Num n={r.l?.a} line={r.l} />
              <Cell line={r.l} className="border-r" />
              <Num n={r.r?.b} line={r.r} />
              <Cell line={r.r} />
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
}

function PatchTable({ patch, split }: { patch: string; split: boolean }) {
  const items = useMemo(() => parsePatch(patch), [patch]);
  return split ? <SplitTable items={items} /> : <UnifiedTable items={items} />;
}

export function DiffStats({ additions, deletions }: { additions: number; deletions: number }) {
  const total = additions + deletions || 1;
  const greens = Math.round((additions / total) * 5);
  const reds = additions + deletions ? 5 - greens : 0;
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
      <span>+{additions}</span>
      <span>−{deletions}</span>
      <span className="inline-flex gap-px">
        {Array.from({ length: 5 }, (_, i) => (
          <i key={i} className={cn("inline-block size-2", i < greens ? "bg-success-emphasis" : i < greens + reds ? "bg-danger-emphasis" : "bg-neutral-muted")} />
        ))}
      </span>
    </span>
  );
}

const STATUS_TONE: Record<string, Tone> = { added: "green", deleted: "red" };

type MdView = "rendered" | "source";
const MD_VIEW_KEY = "bee_md_view";

/** Rendered is the default for markdown; a reviewer who prefers source keeps it across pages. */
function useMdView(): [MdView, (v: MdView) => void] {
  const [view, setView] = useState<MdView>(() => {
    try {
      return localStorage.getItem(MD_VIEW_KEY) === "source" ? "source" : "rendered";
    } catch {
      return "rendered";
    }
  });
  const set = (v: MdView) => {
    try {
      localStorage.setItem(MD_VIEW_KEY, v);
    } catch {
      /* storage unavailable: the choice lasts for this page only */
    }
    setView(v);
  };
  return [view, set];
}

type Layout = "unified" | "split";
const LAYOUT_KEY = "bee_diff_layout";

function useLayout(): [Layout, (v: Layout) => void] {
  const [layout, setLayout] = useState<Layout>(() => {
    try {
      return localStorage.getItem(LAYOUT_KEY) === "split" ? "split" : "unified";
    } catch {
      return "unified";
    }
  });
  const set = (v: Layout) => {
    try {
      localStorage.setItem(LAYOUT_KEY, v);
    } catch {
      /* storage unavailable: the choice lasts for this page only */
    }
    setLayout(v);
  };
  return [layout, set];
}

/** The two commits a diff compares; markdown files need them to render whole documents. */
export type DiffRefs = { repo: string; from?: string; to: string };

const STATUS_TEXT: Record<string, string> = { added: "text-success", deleted: "text-danger" };

/** A navigator row, as in GitHub's file tree: 32px, rounded, the current one marked with a bar on its left. */
const treeRow = cn(
  "relative mx-2 flex h-8 items-center gap-1.5 rounded-md pr-2 text-foreground hover:bg-neutral-muted hover:no-underline",
  "aria-[current=page]:bg-neutral-muted aria-[current=page]:before:absolute aria-[current=page]:before:-left-2 aria-[current=page]:before:inset-y-1.5 aria-[current=page]:before:w-1 aria-[current=page]:before:rounded-md aria-[current=page]:before:bg-info-emphasis",
);

/** Row indent per level; a level's guide line sits under its folder's chevron. */
const indent = (depth: number) => depth * 16 + 4;

/** The review navigator: changed files as a tree of folders that fold, each file linking to its diff below. */
function FileTree({
  nodes,
  byPath,
  viewed,
  current,
  closed,
  onFold,
  onPick,
  depth = 0,
}: {
  nodes: PathNode[];
  byPath: Map<string, DiffFile>;
  viewed: Set<string>;
  current: string | null;
  closed: Set<string>;
  onFold: (dir: string) => void;
  onPick: (path: string) => void;
  depth?: number;
}) {
  return (
    <>
      {nodes.map((n) => {
        if (n.file) {
          const f = byPath.get(n.path);
          return (
            <a
              key={n.path}
              className={treeRow}
              style={{ paddingLeft: indent(depth) + 20 }}
              href={`#diff-${n.path}`}
              title={n.path}
              aria-current={current === n.path ? "page" : undefined}
              onClick={() => onPick(n.path)}
            >
              <FileIcon size={16} className={cn("shrink-0", (f && STATUS_TEXT[f.status]) ?? "text-muted-foreground")} />
              <span className={cn("min-w-0 truncate", f?.status === "deleted" && "line-through", viewed.has(n.path) && "text-muted-foreground")}>{n.name}</span>
              {viewed.has(n.path) && <CheckIcon size={14} className="shrink-0 text-success" />}
              {f && (
                <span className="ml-auto shrink-0 font-mono text-[11px]">
                  {f.additions > 0 && <span className="text-success">+{f.additions}</span>} {f.deletions > 0 && <span className="text-danger">−{f.deletions}</span>}
                </span>
              )}
            </a>
          );
        }
        const open = !closed.has(n.path);
        const Chevron = open ? ChevronDownIcon : ChevronRightIcon;
        const Folder = open ? FileDirectoryOpenFillIcon : FileDirectoryFillIcon;
        return (
          <div key={n.path}>
            <button type="button" className={cn(treeRow, "w-[calc(100%-1rem)] text-left")} style={{ paddingLeft: indent(depth) }} title={n.path} aria-expanded={open} onClick={() => onFold(n.path)}>
              <span className="grid w-4 shrink-0 place-items-center text-muted-foreground">
                <Chevron size={16} />
              </span>
              <Folder size={16} className="shrink-0 text-directory" />
              <span className="ml-0.5 min-w-0 truncate">{n.name}</span>
            </button>
            {open && (
              <div className="relative">
                <span aria-hidden className="pointer-events-none absolute inset-y-0 border-l border-border-muted" style={{ left: indent(depth) + 16 }} />
                <FileTree nodes={n.children} byPath={byPath} viewed={viewed} current={current} closed={closed} onFold={onFold} onPick={onPick} depth={depth + 1} />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

/** docsFirst lists markdown files before code, for changes whose intent should be read first; tree adds a file navigator beside the diffs. */
export function DiffView({ files: all, refs, docsFirst = false, tree = false }: { files: DiffFile[]; refs?: DiffRefs; docsFirst?: boolean; tree?: boolean }) {
  const files = docsFirst ? [...all.filter((f) => isMarkdown(f.path)), ...all.filter((f) => !isMarkdown(f.path))] : all;
  const hasDocs = docsFirst && files.some((f) => isMarkdown(f.path));
  const [mdView, setMdView] = useMdView();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [viewed, setViewed] = useState<Set<string>>(new Set());
  const [layout, setLayout] = useLayout();
  const [filter, setFilter] = useState("");
  // The navigator: whether it shows, which folders are folded, and the file last jumped to.
  const [panel, setPanel] = useState(true);
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const [current, setCurrent] = useState<string | null>(null);
  const add = files.reduce((n, f) => n + f.additions, 0);
  const del = files.reduce((n, f) => n + f.deletions, 0);
  const toggle = (p: string) =>
    setCollapsed((s) => {
      const next = new Set(s);
      if (!next.delete(p)) next.add(p);
      return next;
    });
  // Marking a file viewed folds it away, as on GitHub; unticking opens it again.
  const markViewed = (p: string) => {
    const done = !viewed.has(p);
    setViewed((s) => {
      const next = new Set(s);
      if (done) next.add(p);
      else next.delete(p);
      return next;
    });
    setCollapsed((s) => {
      const next = new Set(s);
      if (done) next.add(p);
      else next.delete(p);
      return next;
    });
  };
  const allCollapsed = files.length > 0 && collapsed.size === files.length;
  const expand = (p: string) =>
    setCollapsed((s) => {
      if (!s.has(p)) return s;
      const next = new Set(s);
      next.delete(p);
      return next;
    });
  // A #diff-<path> link from another tab mounts this view after the browser looked for the anchor.
  useEffect(() => {
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (hash.startsWith("diff-")) document.getElementById(hash)?.scrollIntoView();
  }, []);
  const body = (
    <div className="min-w-0">
      <div className="sticky top-0 z-[3] -mx-1 mt-4 flex flex-wrap items-center gap-3 border-b bg-background px-1 py-2">
        {tree && files.length > 1 && !panel && (
          <Button variant="ghost" size="icon-sm" className="max-lg:hidden" onClick={() => setPanel(true)} aria-label="Show changed files">
            <SidebarCollapseIcon />
          </Button>
        )}
        <span>
          <b>{files.length}</b> file{files.length === 1 ? "" : "s"} changed
        </span>
        <DiffStats additions={add} deletions={del} />
        <span className="ml-auto flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {viewed.size} / {files.length} viewed
          </span>
          <SegmentedControl
            label="Diff layout"
            value={layout}
            onValueChange={(v) => setLayout(v as Layout)}
            items={[
              { value: "unified", label: "Unified" },
              { value: "split", label: "Split" },
            ]}
          />
          {files.length > 1 && (
            <Button variant="ghost" size="sm" onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(files.map((f) => f.path)))}>
              {allCollapsed ? <UnfoldIcon /> : <FoldIcon />} {allCollapsed ? "Expand all" : "Collapse all"}
            </Button>
          )}
        </span>
      </div>
      {hasDocs && <p className="mt-2 text-xs text-muted-foreground">Documents are listed first: read the intent before the code.</p>}
      {files.map((f) => {
        const isCollapsed = collapsed.has(f.path);
        const renderable = !!refs && !f.binary && isMarkdown(f.path);
        const rendered = renderable && mdView === "rendered";
        return (
          <div className="mt-4 scroll-mt-14 rounded-md border bg-background" key={f.path} id={`diff-${f.path}`}>
            <div className={cn("sticky top-[49px] z-[2] flex items-center gap-2 rounded-t-md bg-muted px-3 py-2 font-mono text-[13px]", !isCollapsed ? "border-b" : "rounded-b-md")}>
              <Button variant="ghost" size="icon-sm" onClick={() => toggle(f.path)} aria-label={isCollapsed ? "Expand file" : "Collapse file"}>
                {isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
              </Button>
              <FileIcon className="text-faint" />
              <span className="truncate">{f.path}</span>
              <CopyButton value={f.path} title="Copy path" />
              {f.status !== "modified" && <Badge tone={STATUS_TONE[f.status] ?? "default"}>{f.status}</Badge>}
              <span className="ml-auto flex items-center gap-3">
                {renderable && (
                  <SegmentedControl
                    label="Markdown view"
                    value={mdView}
                    onValueChange={(v) => setMdView(v as MdView)}
                    items={[
                      { value: "rendered", label: "Rendered" },
                      { value: "source", label: "Source" },
                    ]}
                  />
                )}
                <DiffStats additions={f.additions} deletions={f.deletions} />
                <label className="flex cursor-pointer select-none items-center gap-1.5 rounded-md border bg-background px-2 py-0.5 font-sans text-xs">
                  <input type="checkbox" checked={viewed.has(f.path)} onChange={() => markViewed(f.path)} />
                  Viewed
                </label>
              </span>
            </div>
            {!isCollapsed &&
              (f.binary ? (
                <Empty>Binary file not shown</Empty>
              ) : rendered && refs ? (
                <MarkdownDiff repo={refs.repo} from={refs.from} to={refs.to} path={f.path} status={f.status} />
              ) : f.patch ? (
                <div className="overflow-hidden rounded-b-md"><PatchTable patch={f.patch} split={layout === "split"} /></div>
              ) : (
                <Empty>No textual changes</Empty>
              ))}
          </div>
        );
      })}
    </div>
  );
  if (!tree || files.length < 2) return body;
  const shown = filter ? files.filter((f) => f.path.toLowerCase().includes(filter.toLowerCase())) : files;
  const pick = (p: string) => {
    setCurrent(p);
    expand(p);
  };
  const fold = (dir: string) =>
    setClosed((s) => {
      const next = new Set(s);
      if (!next.delete(dir)) next.add(dir);
      return next;
    });
  if (!panel) return body;
  return (
    <div className="grid grid-cols-[300px_minmax(0,1fr)] gap-6 max-lg:grid-cols-[minmax(0,1fr)]">
      {/* The same panel as the code view's file tree: title, filter, then the tree; it stays beside the diffs as they scroll. */}
      <nav aria-label="Changed files" className="sticky top-0 mt-4 flex max-h-screen flex-col self-start rounded-md border text-sm max-lg:hidden">
        <div className="flex h-12 shrink-0 items-center gap-1 px-3">
          <Button variant="ghost" size="icon-sm" onClick={() => setPanel(false)} aria-label="Hide changed files">
            <SidebarExpandIcon />
          </Button>
          <h2 className="text-base font-semibold">Files</h2>
          <span className="ml-auto text-xs text-muted-foreground">{files.length}</span>
        </div>
        <div className="shrink-0 px-3 pb-3">
          <label className="flex h-8 items-center gap-2 rounded-md border bg-background px-2 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-ring">
            <SearchIcon size={16} className="shrink-0 text-muted-foreground" />
            <input
              className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
              placeholder="Filter files"
              aria-label="Filter changed files"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setFilter("")}
            />
          </label>
        </div>
        <div className="min-h-0 overflow-auto pb-3">
          {shown.length ? (
            <FileTree
              nodes={pathTree(shown.map((f) => f.path))}
              byPath={new Map(files.map((f) => [f.path, f]))}
              viewed={viewed}
              current={current}
              closed={filter ? new Set() : closed}
              onFold={fold}
              onPick={pick}
            />
          ) : (
            <p className="px-4 text-xs text-faint">No matching files</p>
          )}
        </div>
      </nav>
      {body}
    </div>
  );
}
